
import { Directive, OnDestroy } from "@angular/core";
import { Subject, Subscription } from 'rxjs';

/**
 * Base class for managing component subscriptions and preventing memory leaks.
 *
 * Provides two strategies for subscription management:
 *
 * 1. **takeUntil pattern (RECOMMENDED)**:
 *    Use the `destroy$` Subject with RxJS `takeUntil` operator:
 *    ```typescript
 *    this.myService.getData()
 *      .pipe(takeUntil(this.destroy$))
 *      .subscribe(data => { ... });
 *    ```
 *
 * 2. **Manual subscription array (LEGACY)**:
 *    Push subscriptions to the array for manual cleanup:
 *    ```typescript
 *    this.subscriptions.push(
 *      this.myService.getData().subscribe(...)
 *    );
 *    ```
 *
 * @example
 * ```typescript
 * export class MyComponent extends SubscriptionManager implements OnInit, OnDestroy {
 *   ngOnInit() {
 *     // Preferred: takeUntil pattern
 *     this.dataService.stream$
 *       .pipe(takeUntil(this.destroy$))
 *       .subscribe(data => this.data = data);
 *   }
 *
 *   ngOnDestroy() {
 *     super.ngOnDestroy(); // Don't forget to call super!
 *   }
 * }
 * ```
 */
@Directive()
export class SubscriptionManager implements OnDestroy {

  /**
   * Subject that emits when the component is destroyed.
   * Use with takeUntil() operator to automatically unsubscribe.
   *
   * @example
   * this.service.getData()
   *   .pipe(takeUntil(this.destroy$))
   *   .subscribe(data => { ... });
   */
  protected destroy$ = new Subject<void>();

  /**
   * Array of subscriptions to be cleaned up on destroy.
   * Use this for subscriptions that can't use takeUntil pattern.
   *
   * @deprecated Prefer using destroy$ with takeUntil operator
   */
  public subscriptions: Subscription[] = [];

  /**
   * Cleanup method called when component is destroyed.
   * Completes the destroy$ Subject and unsubscribes from all manual subscriptions.
   *
   * IMPORTANT: If you override this in your component, call super.ngOnDestroy()!
   */
  ngOnDestroy(): void {
    // Emit to complete all takeUntil subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    // Unsubscribe from manual subscriptions (legacy support)
    this.subscriptions.forEach(sub => sub?.unsubscribe());
  }
}
